import React from "react";

function SuccessIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 12.5l4.5 4.5L19 7.5"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        d="M7 7l10 10M17 7L7 17"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3l9 17H3L12 3z"
      />
      <path
        strokeLinecap="round"
        d="M12 9v4M12 16h.01"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path
        strokeLinecap="round"
        d="M12 10v6M12 7h.01"
      />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`h-4 w-4 transition-transform ${
        open ? "rotate-180" : ""
      }`}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.07l3.71-3.84a.75.75 0 111.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function normalizeStatus(value) {
  const status = String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    [
      "valid",
      "success",
      "passed",
      "pass",
      "ok",
      "valid_blueprint",
    ].includes(status)
  ) {
    return "success";
  }

  if (
    [
      "warning",
      "warnings",
      "partial",
      "valid_with_warnings",
    ].includes(status)
  ) {
    return "warning";
  }

  if (
    [
      "invalid",
      "error",
      "errors",
      "failed",
      "fail",
    ].includes(status)
  ) {
    return "error";
  }

  return "info";
}

function getMessage(item) {
  if (typeof item === "string") {
    return item;
  }

  return (
    item?.message ||
    item?.error ||
    item?.description ||
    item?.detail ||
    item?.reason ||
    "Validation issue detected."
  );
}

function getItemName(item) {
  if (typeof item === "string") {
    return "";
  }

  return (
    item?.sectionName ||
    item?.groupName ||
    item?.questionType ||
    item?.name ||
    item?.title ||
    item?.field ||
    ""
  );
}

function getItems(result, type) {
  const candidates =
    type === "errors"
      ? [
          result?.errors,
          result?.validationErrors,
          result?.issues?.errors,
        ]
      : [
          result?.warnings,
          result?.validationWarnings,
          result?.issues?.warnings,
        ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

export default function BlueprintValidationResult({
  result = null,
  loading = false,
  error = null,
  onRetry,
  onDismiss,
  onIssueClick,
  title = "Blueprint Validation",
  showSummary = true,
  showDetails = true,
  initiallyExpanded = true,
  compact = false,
}) {
  const [expanded, setExpanded] =
    React.useState(initiallyExpanded);

  if (loading) {
    return (
      <div className="w-full rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-100" />

          <div className="flex-1">
            <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-3 w-64 animate-pulse rounded bg-gray-100" />
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
          <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage =
      typeof error === "string"
        ? error
        : error?.message ||
          error?.data?.message ||
          "Blueprint validation could not be completed.";

    return (
      <div className="w-full rounded-xl border border-red-200 bg-red-50 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <ErrorIcon />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-red-900">
              Validation failed
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              {errorMessage}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
                >
                  Try Again
                </button>
              )}

              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-50"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const status = normalizeStatus(
    result.status ??
      result.validationStatus ??
      (result.valid === true
        ? "valid"
        : result.valid === false
        ? "invalid"
        : "")
  );

  const errors = getItems(
    result,
    "errors"
  );

  const warnings = getItems(
    result,
    "warnings"
  );

  const messages = Array.isArray(
    result.messages
  )
    ? result.messages
    : [];

  const normalizedMessages =
    messages.filter(Boolean);

  const errorCount =
    Number(
      result.errorCount ??
        result.errorsCount
    ) || errors.length;

  const warningCount =
    Number(
      result.warningCount ??
        result.warningsCount
    ) || warnings.length;

  const totalIssues =
    errorCount + warningCount;

  const statusConfig = {
    success: {
      title:
        result.title ||
        "Blueprint is valid",
      description:
        result.message ||
        result.summary ||
        "All blueprint validation checks passed successfully.",
      wrapper:
        "border-emerald-200 bg-emerald-50",
      iconWrapper:
        "bg-emerald-100 text-emerald-600",
      titleColor:
        "text-emerald-900",
      textColor:
        "text-emerald-700",
      icon: <SuccessIcon />,
    },
    warning: {
      title:
        result.title ||
        "Blueprint is valid with warnings",
      description:
        result.message ||
        result.summary ||
        "The blueprint can be used, but some configuration items should be reviewed.",
      wrapper:
        "border-amber-200 bg-amber-50",
      iconWrapper:
        "bg-amber-100 text-amber-600",
      titleColor:
        "text-amber-900",
      textColor:
        "text-amber-700",
      icon: <WarningIcon />,
    },
    error: {
      title:
        result.title ||
        "Blueprint needs attention",
      description:
        result.message ||
        result.summary ||
        "One or more validation checks failed.",
      wrapper:
        "border-red-200 bg-red-50",
      iconWrapper:
        "bg-red-100 text-red-600",
      titleColor:
        "text-red-900",
      textColor:
        "text-red-700",
      icon: <ErrorIcon />,
    },
    info: {
      title:
        result.title || title,
      description:
        result.message ||
        result.summary ||
        "Validation information is available for this blueprint.",
      wrapper:
        "border-blue-200 bg-blue-50",
      iconWrapper:
        "bg-blue-100 text-blue-600",
      titleColor:
        "text-blue-900",
      textColor:
        "text-blue-700",
      icon: <InfoIcon />,
    },
  }[status];

  const renderIssue = (
    item,
    index,
    issueType
  ) => {
    const name = getItemName(item);
    const message = getMessage(item);

    return (
      <button
        key={`${issueType}-${index}`}
        type="button"
        onClick={() =>
          onIssueClick?.(
            item,
            issueType,
            index
          )
        }
        disabled={!onIssueClick}
        className={`w-full rounded-lg border p-3 text-left transition ${
          issueType === "error"
            ? "border-red-100 bg-red-50/60"
            : "border-amber-100 bg-amber-50/60"
        } ${
          onIssueClick
            ? "cursor-pointer hover:shadow-sm"
            : "cursor-default"
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
              issueType === "error"
                ? "bg-red-100 text-red-600"
                : "bg-amber-100 text-amber-600"
            }`}
          >
            {issueType === "error" ? (
              <ErrorIcon />
            ) : (
              <WarningIcon />
            )}
          </div>

          <div className="min-w-0 flex-1">
            {name && (
              <p
                className={`text-xs font-semibold ${
                  issueType === "error"
                    ? "text-red-800"
                    : "text-amber-800"
                }`}
              >
                {name}
              </p>
            )}

            <p
              className={`text-xs leading-5 ${
                name ? "mt-0.5" : ""
              } ${
                issueType === "error"
                  ? "text-red-700"
                  : "text-amber-700"
              }`}
            >
              {message}
            </p>

            {typeof item !==
              "string" &&
              item?.path && (
                <p className="mt-1 font-mono text-[10px] text-gray-400">
                  {Array.isArray(
                    item.path
                  )
                    ? item.path.join(
                        " → "
                      )
                    : item.path}
                </p>
              )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="w-full">
      <div
        className={`rounded-xl border p-4 ${
          statusConfig.wrapper
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${statusConfig.iconWrapper}`}
          >
            {statusConfig.icon}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className={`text-sm font-semibold ${statusConfig.titleColor}`}
              >
                {statusConfig.title}
              </h3>

              {result.code && (
                <span className="rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] font-medium text-gray-500">
                  {result.code}
                </span>
              )}
            </div>

            <p
              className={`mt-1 text-xs leading-5 ${statusConfig.textColor}`}
            >
              {statusConfig.description}
            </p>
          </div>

          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className={`shrink-0 rounded-lg p-1.5 transition ${statusConfig.textColor} hover:bg-white/50`}
              aria-label="Dismiss validation result"
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {showSummary && (
        <div
          className={`mt-3 grid gap-3 ${
            compact
              ? "grid-cols-2"
              : "grid-cols-2 sm:grid-cols-4"
          }`}
        >
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Status
            </p>

            <p
              className={`mt-1 text-sm font-bold ${
                status === "success"
                  ? "text-emerald-600"
                  : status === "warning"
                  ? "text-amber-600"
                  : status === "error"
                  ? "text-red-600"
                  : "text-blue-600"
              }`}
            >
              {status === "success"
                ? "Valid"
                : status === "warning"
                ? "Review"
                : status === "error"
                ? "Invalid"
                : "Info"}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Errors
            </p>

            <p className="mt-1 text-sm font-bold text-red-600">
              {errorCount}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Warnings
            </p>

            <p className="mt-1 text-sm font-bold text-amber-600">
              {warningCount}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Checks
            </p>

            <p className="mt-1 text-sm font-bold text-gray-700">
              {result.checkCount ??
                result.totalChecks ??
                result.validatedChecks ??
                "—"}
            </p>
          </div>
        </div>
      )}

      {showDetails &&
        (errors.length > 0 ||
          warnings.length > 0 ||
          normalizedMessages.length >
            0) && (
          <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() =>
                setExpanded(
                  (value) => !value
                )
              }
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-gray-50"
            >
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Validation Details
                </p>

                <p className="mt-0.5 text-xs text-gray-500">
                  {totalIssues > 0
                    ? `${totalIssues} issue${
                        totalIssues === 1
                          ? ""
                          : "s"
                      } require attention.`
                    : `${normalizedMessages.length} validation message${
                        normalizedMessages.length ===
                        1
                          ? ""
                          : "s"
                      }.`}
                </p>
              </div>

              <ChevronIcon
                open={expanded}
              />
            </button>

            {expanded && (
              <div className="space-y-3 border-t border-gray-100 p-4">
                {errors.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      <h4 className="text-xs font-semibold text-red-700">
                        Errors
                      </h4>

                      <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                        {errors.length}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {errors.map(
                        (
                          item,
                          index
                        ) =>
                          renderIssue(
                            item,
                            index,
                            "error"
                          )
                      )}
                    </div>
                  </div>
                )}

                {warnings.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-amber-500" />
                      <h4 className="text-xs font-semibold text-amber-700">
                        Warnings
                      </h4>

                      <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600">
                        {warnings.length}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {warnings.map(
                        (
                          item,
                          index
                        ) =>
                          renderIssue(
                            item,
                            index,
                            "warning"
                          )
                      )}
                    </div>
                  </div>
                )}

                {normalizedMessages.length >
                  0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-blue-500" />
                      <h4 className="text-xs font-semibold text-blue-700">
                        Messages
                      </h4>
                    </div>

                    <div className="space-y-2">
                      {normalizedMessages.map(
                        (
                          item,
                          index
                        ) =>
                          renderIssue(
                            item,
                            index,
                            "message"
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      {showDetails &&
        errors.length === 0 &&
        warnings.length === 0 &&
        normalizedMessages.length ===
          0 && (
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <SuccessIcon />
            </div>

            <div>
              <p className="text-xs font-semibold text-emerald-900">
                No validation issues found
              </p>

              <p className="mt-0.5 text-xs text-emerald-700">
                The blueprint passed the available
                validation checks.
              </p>
            </div>
          </div>
        )}
    </div>
  );
}